import Asset from '@react-spectrum/s2/icons/Asset';
import type { ComponentType, ReactNode } from 'react';
import { Fragment, useEffect, useState } from 'react';
import { Focusable, Tooltip, TooltipTrigger } from 'react-aria-components';

import { cn } from '@/utils/cn';

import type {
	MetricBasis,
	ProvenanceRef,
	StrategyDefinition,
} from '../../../../bridge/types/strategy.ts';

import {
	basisLabel,
	formatDuration,
	formatDurationCoarse,
	glossField,
	humanize,
	metricGoalProgress,
} from './strategyFormat';
import type { GlossSegment } from './strategyFormat';

// The small uppercase heading that names a group inside a card ("The move", "What we
// built", "Your part"). One component rather than one per file: it appeared twice under
// two names the moment a second surface needed it, and two identical labels drift.
export function SectionLabel({ children }: { children: ReactNode }) {
	return (
		<p
			className="text-xs font-semibold tracking-wide text-(--text-muted)
				uppercase"
		>
			{children}
		</p>
	);
}

// Live-ticking elapsed-time label for an in-flight run: 1s tick by default, or `coarse`
// (10s tick, minute-granular) for the calmer list. A NaN startedAt renders nothing.
export function LiveDuration({
	startedAt,
	coarse = false,
}: {
	startedAt: string;
	coarse?: boolean;
}) {
	const [now, setNow] = useState(() => Date.now());
	useEffect(() => {
		const id = setInterval(() => setNow(Date.now()), coarse ? 10_000 : 1_000);
		return () => clearInterval(id);
	}, [coarse]);

	const started = Date.parse(startedAt);
	if (Number.isNaN(started)) return null;
	const label = (coarse ? formatDurationCoarse : formatDuration)(now - started);
	return <span title={startedAt}>{label}</span>;
}

// Renders one prose field, wrapping each glossed term (per its canonical site) with a
// dotted underline + hover/focus tooltip. RAC Focusable is required: a bare span in
// TooltipTrigger is inert, so hover/focus/aria wouldn't reach it.
export function GlossedText({
	where,
	text,
	definitions,
	sites,
}: {
	where: string;
	text: string;
	definitions: StrategyDefinition[] | undefined;
	sites: Map<string, string>;
}) {
	if (!definitions?.length || sites.size === 0) return <>{text}</>;
	const segments: GlossSegment[] = glossField(where, text, definitions, sites);
	if (segments.every((s) => s.kind === 'plain')) return <>{text}</>;
	return (
		<>
			{segments.map((seg, i) =>
				seg.kind === 'plain' ? (
					<Fragment key={i}>{seg.text}</Fragment>
				) : (
					<TooltipTrigger key={i} delay={300}>
						<Focusable>
							<span
								role="button"
								tabIndex={0}
								className="decoration-beacon-stroke/60 cursor-help underline
									decoration-dotted underline-offset-2"
							>
								{seg.text}
							</span>
						</Focusable>
						<Tooltip
							placement="top"
							offset={6}
							className="z-50 max-w-xs rounded-md bg-gray-800 px-2.5 py-1.5
								text-xs leading-snug text-white shadow-md"
						>
							<span className="font-semibold">{seg.term}:</span> {seg.plain}
						</Tooltip>
					</TooltipTrigger>
				),
			)}
		</>
	);
}

// Warm icon medallion anchoring a card header. NOT accent-tinted — blue stays reserved for
// pointing, so medallions differ by icon shape. Decorative (aria-hidden); the label means it.
export function IconMedallion({
	icon: Icon,
	className,
}: {
	icon: ComponentType;
	className?: string;
}) {
	return (
		<span
			aria-hidden
			className={cn(
				`bg-beacon-light-gray grid size-9 shrink-0 place-items-center rounded-lg
				text-(--text-muted) [--iconPrimary:currentColor] [&_svg]:size-5`,
				className,
			)}
		>
			<Icon />
		</span>
	);
}

// PRIMARY north-star goal gauge — the one deliberate use of the gold brand signature.
// Hand-rolled SVG sanctioned in design.md (S2's Meter is variant-fixed to blue/green). Full
// ring is the destination frame (no false progress); a darker arc overlays only with a baseline.
export function MetricGauge({
	target,
	baseline,
	current,
	size = 132,
	children,
}: {
	target: number;
	baseline?: number;
	// The latest MEASURED value, when we have one. It supersedes `baseline` for the arc:
	// baseline is where the owner started, current is where they are, and drawing the
	// starting line when a real measurement exists would understate their progress.
	current?: number | null;
	// Ring diameter in px (default 132 for the detail view; cards pass a smaller value).
	size?: number;
	children: React.ReactNode;
}) {
	const measured = typeof current === 'number' && Number.isFinite(current);
	// metricGoalProgress requires baseline < target, so a value that has REACHED the target
	// would fall back to no arc at all — blanking the ring at the moment it should be full.
	const reached = measured && (current as number) >= target && target > 0;
	const { pct, hasStart } = reached
		? { pct: 100, hasStart: true }
		: measured
			? metricGoalProgress(target, current as number)
			: metricGoalProgress(target, baseline);
	const stroke = Math.round(size * 0.0758);
	const r = (size - stroke) / 2;
	const c = 2 * Math.PI * r;
	const arc = (Math.max(0, Math.min(100, pct)) / 100) * c;
	return (
		<div className="relative shrink-0" style={{ width: size, height: size }}>
			<svg
				aria-hidden
				width={size}
				height={size}
				viewBox={`0 0 ${size} ${size}`}
				className="block -rotate-90"
			>
				<circle
					cx={size / 2}
					cy={size / 2}
					r={r}
					fill="none"
					stroke="var(--color-beacon-canary)"
					strokeOpacity={0.32}
					strokeWidth={stroke}
				/>
				{hasStart && (
					<circle
						cx={size / 2}
						cy={size / 2}
						r={r}
						fill="none"
						stroke="var(--color-beacon-canary)"
						strokeWidth={stroke}
						strokeLinecap="round"
						strokeDasharray={`${arc} ${c - arc}`}
					/>
				)}
			</svg>
			<div className="absolute inset-0 grid place-items-center">{children}</div>
		</div>
	);
}

// Slim static baseline→target bar for SUPPORTING signals (NOT live progress); renders only
// with a baseline. Muted fill, not gold, keeps it subordinate to the primary's gauge.
export function MetricProgressBar({
	baseline,
	target,
}: {
	baseline: number;
	target: number;
}) {
	const { pct } = metricGoalProgress(target, baseline);
	return (
		<div
			aria-hidden
			className="bg-beacon-light-gray h-1.5 w-full overflow-hidden rounded-full"
		>
			<div
				className="h-full rounded-full bg-(--text-muted)"
				style={{ width: `${pct}%` }}
			/>
		</div>
	);
}

// Plain-language note on where a number came from — revealed with "Show sources".
export function BasisNote({ basis }: { basis: MetricBasis }) {
	return (
		<p className="mt-1.5 text-sm text-(--text-muted)">
			Target drawn from {basisLabel(basis.source)}
			{basis.quote ? <span className="italic">: “{basis.quote}”</span> : null}
		</p>
	);
}

// Opt-in evidence panels: external examples (pattern steals + research citations) vs the
// owner's own context (business-context), shown as two distinct groups.
export function ProvChips({
	provenance,
	show,
}: {
	provenance: ProvenanceRef[] | undefined;
	show: boolean;
}) {
	if (!show || !provenance?.length) return null;
	const examples = provenance.filter(
		(p) => p.kind === 'pattern' || p.kind === 'research',
	);
	const ownContext = provenance.filter((p) => p.kind === 'business-context');
	if (examples.length === 0 && ownContext.length === 0) return null;
	return (
		<div className="mt-2 space-y-2.5">
			{examples.length > 0 && (
				<div className="space-y-1.5">
					<p className="text-xs font-semibold text-(--text-muted)">
						Other examples
					</p>
					{examples.map((p, i) => (
						<div
							key={i}
							className="glass-panel space-y-1 p-3 text-sm
								text-(--text-secondary)"
						>
							{p.kind === 'pattern' && (
								<>
									{p.sourceUrl ? (
										<p className="font-medium text-(--text-primary)">
											<a
												className="text-beacon-accent underline"
												href={p.sourceUrl}
												target="_blank"
												rel="noreferrer"
											>
												{p.interviewTitle}
											</a>
										</p>
									) : (
										<p className="font-medium text-(--text-primary)">
											{p.interviewTitle}
										</p>
									)}
									<p className="text-(--text-muted) italic">
										“{p.evidenceQuote}”
									</p>
									<p>
										<strong className="font-semibold">Why it fits:</strong>{' '}
										{p.whyItFits}
									</p>
									<p>
										<strong className="font-semibold">What we adapted:</strong>{' '}
										{p.whatWeAdapted}
									</p>
								</>
							)}
							{p.kind === 'research' && (
								<>
									<p className="font-medium">
										<a
											className="text-beacon-accent underline"
											href={p.url}
											target="_blank"
											rel="noreferrer"
										>
											{p.title ?? p.url}
										</a>
									</p>
									<p className="text-(--text-muted) italic">“{p.quote}”</p>
									<p>{p.why}</p>
								</>
							)}
						</div>
					))}
				</div>
			)}
			{ownContext.length > 0 && (
				<div className="space-y-1">
					<p className="text-xs font-semibold text-(--text-muted)">
						Drawn from your own context
					</p>
					{ownContext.map((p, i) => (
						<p key={i} className="text-sm text-(--text-muted)">
							{p.kind === 'business-context' ? p.note : null}
						</p>
					))}
				</div>
			)}
		</div>
	);
}

// An artifact-type chip shown under a step. artifactType is possibly-absent on a loose
// LLM plan (aliased to `type`); render nothing rather than an empty chip.
export function ArtifactChip({
	artifactType,
}: {
	artifactType?: string | null;
}) {
	if (!artifactType) return null;
	return (
		<span
			title={humanize(artifactType)}
			className={cn(
				`border-beacon-stroke inline-flex shrink-0 items-center gap-1
				rounded-full border bg-white px-2 py-0.5 text-xs text-(--text-muted)`,
			)}
		>
			<span aria-hidden className="[--iconPrimary:currentColor] [&_svg]:size-3">
				<Asset />
			</span>
			{artifactType}
		</span>
	);
}
